import React, {
    useCallback,
    useState,
    forwardRef,
    MutableRefObject,
    useEffect,
} from 'react';
import {Placement} from 'react-bootstrap/esm/types';
import {Overlay, Tooltip} from 'react-bootstrap';
import {uniqueId} from 'lodash';
import {isMobile, isTablet} from 'react-device-detect';
import styled from 'styled-components';

export enum OverlayType {
    ToolTip,
    Popover,
}

type OverlayTriggerProps = {
    placement?: Placement;
    overlay?: string | JSX.Element | JSX.Element[];
    children: JSX.Element;
    className?: string;
    as?: OverlayType;
    delay?: number;
    allowPropagation?: boolean;
    controlledShow?: boolean;
    // keeps the trigger mounted but never opens it, for callers whose element
    // tree must not change as the overlay comes and goes
    disabled?: boolean;
};

export const OverlayTrigger = forwardRef<HTMLElement, OverlayTriggerProps>(
    function OverlayTrigger(
        {
            placement = 'top-end',
            overlay,
            children,
            className = '',
            delay = 200,
            allowPropagation = false,
            controlledShow = false,
            disabled = false,
        },
        ref: MutableRefObject<HTMLElement>
    ) {
        const [show, setShow] = useState(controlledShow);

        const target = React.useRef<HTMLElement>(null);

        let showTimeout: NodeJS.Timeout;

        const hideTimeout = React.useRef<NodeJS.Timeout | null>(null);

        useEffect(() => {
            if (!show || (!isMobile && !isTablet)) return;

            hideTimeout.current = setTimeout(() => {
                setShow(false);
            }, 1500);

            return () => {
                if (hideTimeout.current) {
                    clearTimeout(hideTimeout.current);
                    hideTimeout.current = null;
                }
            };
        }, [show]);

        const onMouseEnter = useCallback(() => {
            if (isMobile || isTablet) return;

            clearTimeout(showTimeout);
            showTimeout = setTimeout(() => setShow(true), delay);
        }, []);

        const onMouseLeave = useCallback(() => {
            if (isMobile || isTablet) return;

            clearTimeout(showTimeout);
            setShow(false);
        }, []);

        const onMouseDown = useCallback(() => {
            if (isMobile || isTablet) return;

            clearTimeout(showTimeout);
            setShow(false);
        }, []);

        const onClick = useCallback(
            (event: React.MouseEvent) => {
                if (!allowPropagation) {
                    event.stopPropagation();
                }

                if (isMobile || isTablet) {
                    event.preventDefault();
                    event.stopPropagation();
                    if (hideTimeout.current) {
                        clearTimeout(hideTimeout.current);
                        hideTimeout.current = null;
                    }
                    setShow((previousState) => !previousState);
                }
            },
            [allowPropagation, isMobile, isTablet]
        );

        useEffect(() => {
            setShow(controlledShow);
        }, [controlledShow]);

        if (typeof overlay === 'undefined' || overlay == '') {
            return children;
        }

        return (
            <>
                {React.Children.map(children, (child) =>
                    React.cloneElement(child, {
                        ref: ref ?? target,
                        onMouseEnter,
                        onMouseLeave,
                        onMouseDown,
                        onClick: (event: React.MouseEvent) => {
                            onClick(event);

                            if (React.isValidElement(child) && child.props) {
                                const childProps =
                                    child.props as React.PropsWithChildren<{
                                        onClick?: (
                                            event: React.MouseEvent
                                        ) => void;
                                    }>;

                                if (childProps.onClick) {
                                    childProps.onClick(event);
                                }
                            }
                        },
                    })
                )}
                <Overlay
                    target={ref ? ref.current : target.current}
                    flip={true}
                    show={disabled ? false : show}
                    placement={placement}>
                    {(props) => (
                        <RevampedTooltip
                            $placement={placement}
                            className={className}
                            id={uniqueId('tooltip')}
                            {...props}>
                            {overlay}
                        </RevampedTooltip>
                    )}
                </Overlay>
            </>
        );
    }
);

const RevampedTooltip = styled(Tooltip)<{$placement: string}>`
    > .tooltip-arrow {
        &::before {
            ${({$placement}) => {
                // NOTE: do the same for other directions
                if ($placement.startsWith('top')) {
                    return 'border-top-color: #dadedf;';
                }
            }}
        }
    }
`;
